import React, { useEffect } from "react";
import { Form, Formik } from "formik";
import { Form as FormLayout } from "react-bootstrap";
import * as Yup from "yup";
import TextInput from "../../Common/components/text-input";
import DateInput from "../../Common/components/date-input";
import { Card, CardBody, CardFooter, CardHeader } from "../../../../_metronic/_partials/controls";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as handlers from "../../UserManagement/_redux/users/usersHandlers"
import cogoToast from "cogo-toast";
import CheckBoxInput from "../../Common/components/checkbox-input";
import { SearchIcon, UserIcon } from "../../../util/Icons";
import { debounce, ucFirst } from "../../../util/Helpers";
import { usePayoutSelectiveDividendsMutation } from "../../../../redux/apiSlice";

export const SelectiveDividendPayout = ({ instrumentId, symbol }) => {
    const [
        payoutDividends,
        {
            isLoading,
        }
    ] = usePayoutSelectiveDividendsMutation()

    const users = useSelector(({ users }) => users, shallowEqual);
    const dispatch = useDispatch();

    /* User Search Input */
    const searchUser = e => {
        const query = e.target.value.trim();
        if (query !== "") {
            dispatch(handlers.searchUsers(query));
        }
    }

    /* Notification Pop up */
    useEffect(() => {
        if (users.notification) {
            cogoToast[users.notification.type](users.notification.message);
            dispatch(handlers.clearNotification());
        }
    });

    const toggleUserSelected = (user, setFieldValue, selectedUsers) => {
        const id = user.userId
        if (selectedUsers[id]) {
            const newUsers = { ...selectedUsers }
            delete newUsers[id]
            setFieldValue("users", newUsers)
        } else {
            setFieldValue(`users[${id}]`, user)
        }
    }

    const UsersList = ({ users, selectedUsers, setFieldValue }) => users?.map((user) => (
        <div className="mb-3" key={user.userId}>
            <CheckBoxInput
                name={`users[${user.userId}]`}
                onChange={() => toggleUserSelected(user, setFieldValue, selectedUsers)}
                checked={selectedUsers[user.userId]}
                value={user}
            >
                <div className="d-flex align-items-center">
                    <div
                        style={{
                            height: "28px",
                            width: "28px",
                        }}
                        className="mr-3 border rounded-circle bg-black d-flex align-items-center justify-content-center"
                    >
                        <UserIcon size="18px" />
                    </div>
                    <div className="col px-0 flex-column align-items-start">
                        <div>
                            {`${user.firstName} ${user.lastName}`.substring(0, 20)}
                        </div>
                        <div>
                            {user.email.substr(0, 20)}
                        </div>
                        <div className="text-muted">
                            =&gt; {ucFirst(user.userGroupName?.substring(0, 20))}
                        </div>
                    </div>
                </div>
            </CheckBoxInput>
        </div>
    ));

    return (
        <Formik
            initialValues={{
                amountPerShare: 0.0,
                qualificationDate: "",
                users: {}
            }}
            validationSchema={Yup.object({
                amountPerShare: Yup.number().min(0.0).required('Required'),
                qualificationDate: Yup.date().required('Required'),
                users: Yup.object()
                    .test('users length', 'At least one user must be selected', users => {
                        const keys = Object.keys(users)
                        return keys.length > 0
                    })
            })}
            onSubmit={async (values) => {
                const users = []
                for (const userId of Object.keys(values.users || {})) {
                    users.push({ userId: userId, userGroup: values?.users[userId]?.userGroup })
                }

                payoutDividends({
                    assetId: instrumentId,
                    amountPerShare: values.amountPerShare,
                    qualificationDate: values.qualificationDate,
                    users,
                })
                .unwrap()
                .then(() => {
                    cogoToast.success("Dividends Payment Initiated Successfully")
                })
            }}
        >
            {({ isValid, values, setFieldValue }) => (
                <Form className="h-100">
                    <Card className="h-100 mb-0">
                        <CardHeader title={`Payout ${symbol || instrumentId} Dividends`} />
                        <CardBody className="h-100 d-flex flex-column">
                            <FormLayout.Row>
                                <TextInput
                                    label="Amount Per Share*"
                                    name="amountPerShare"
                                    type="number"
                                    step="any"
                                    min="0.0"
                                />
                                <DateInput
                                    label="Qualification Date*"
                                    name="qualificationDate"
                                />
                            </FormLayout.Row>
                            <h5>Select Users</h5>
                            <FormLayout.Row>
                                <div className="col">
                                    <div className="d-flex mb-6">
                                        <div className="input-icon h-40px">
                                            <input
                                                type="text"
                                                className="form-control form-control-lg form-control-solid h-40px"
                                                placeholder="Search Users"
                                                autoComplete="off"
                                                onInput={debounce(searchUser, 1000)}
                                            />
                                            <span>
                                                {
                                                    users.isLoading ?
                                                        <div className="spinner-border spinner-border-sm text-primary"
                                                            role="status">
                                                            <span className="sr-only">Loading...</span>
                                                        </div>
                                                        :
                                                        <span className="svg-icon svg-icon-lg">
                                                            <SearchIcon />
                                                        </span>
                                                }
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="col">
                                    <div className="mb-10 h3 font-weight-bold text-dark">Selected Users</div>
                                </div>
                            </FormLayout.Row>
                            <FormLayout.Row className="h-100 overflow-auto">
                                <div className="col">
                                    <UsersList users={users?.data} selectedUsers={values.users} setFieldValue={setFieldValue} />
                                </div>
                                <div className="col">
                                    <UsersList users={Object.values(values.users)} selectedUsers={values.users} setFieldValue={setFieldValue} />
                                </div>
                            </FormLayout.Row>
                        </CardBody>
                        <CardFooter>
                            <button
                                type="submit"
                                className="btn btn-primary btn-elevate px-4"
                                disabled={isLoading || !isValid}
                            >
                                Payout Dividends
                            </button>
                        </CardFooter>
                    </Card>
                </Form>
            )}
        </Formik>
    )
}